import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, UploadSimple, Sparkle, Target, ShieldCheck, Compass, CheckCircle, MapPin, Briefcase, ArrowSquareOut } from "@phosphor-icons/react";
import Header from "@/components/app/Header";
import { api } from "@/lib/api";

const steps = [
  { num: "01", title: "Drop your resume", body: "Upload a PDF/DOCX or fill in a short form. We extract your skills, projects, seniority and direction.", icon: UploadSimple },
  { num: "02", title: "We surface jobs", body: "Fresh remote roles are pulled from live sources and filtered against your goals and location preferences.", icon: Compass },
  { num: "03", title: "AI scores each match", body: "Every listing gets a 0–100 score, a reason, matching skills and identified gaps — no spam, no guessing.", icon: Target },
  { num: "04", title: "You ship applications", body: "A tailored, editable draft is ready for every job. Copy, review, tweak, then apply on the company site.", icon: Sparkle },
];

function LiveJobCard({ job }) {
  const tags = (job.tags || []).filter((t) => typeof t === "string").slice(0, 3);
  return (
    <div className="border border-slate-200/60 bg-white/50 backdrop-blur-sm shadow-sm bg-white flex flex-col justify-between hover:border-slate-200 transition-colors">
      <div className="p-5">
        <div className="flex items-start gap-3 mb-3">
          {job.company_logo ? (
            <img
              src={job.company_logo}
              alt={job.company_name}
              className="w-9 h-9 object-contain border border-slate-200/60 bg-white/50 backdrop-blur-sm shadow-sm flex-shrink-0"
              onError={(e) => { e.target.style.display = "none"; }}
            />
          ) : (
            <div className="w-9 h-9 border border-slate-200/60 bg-white/50 backdrop-blur-sm shadow-sm flex-shrink-0 bg-slate-50 flex items-center justify-center font-mono text-xs font-bold text-ink-mute">
              {(job.company_name || "?")[0].toUpperCase()}
            </div>
          )}
          <div className="min-w-0">
            <p className="font-display font-bold text-sm leading-tight text-ink line-clamp-2">{job.title}</p>
            <p className="text-xs text-ink-soft mt-0.5">{job.company_name}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 text-[11px] text-ink-mute mt-3">
          {job.location && (
            <span className="inline-flex items-center gap-1">
              <MapPin size={10} weight="fill" /> {job.location}
            </span>
          )}
          {job.job_type && (
            <span className="inline-flex items-center gap-1">
              <Briefcase size={10} weight="fill" /> {job.job_type}
            </span>
          )}
        </div>
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {tags.map((t) => (
              <span key={t} className="px-2 py-0.5 bg-slate-50 border border-slate-200/60 bg-white/50 backdrop-blur-sm shadow-sm text-[10px] font-mono text-ink-soft uppercase tracking-wide">
                {t}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="px-5 py-3 border-t border-border flex items-center justify-between">
        <span className="font-mono text-[10px] text-ink-mute uppercase">{job.source || "remote"}</span>
        <a href={job.url} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-[11px] font-semibold text-brand hover:underline">
          View job <ArrowSquareOut size={11} weight="bold" />
        </a>
      </div>
    </div>
  );
}

import { useAuth } from "@/context/AuthContext";

export default function Landing() {
  const { user } = useAuth();
  const [liveJobs, setLiveJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(true);

  useEffect(() => {
    api.get(`/jobs/preview?limit=6`)
      .then((r) => setLiveJobs(r.data.jobs || []))
      .catch(() => setLiveJobs([]))
      .finally(() => setJobsLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* HERO */}
      <section className="relative border-b border-border overflow-hidden">
        <div className="absolute inset-0 grid-lines-bg opacity-70 pointer-events-none" />
        <div className="noise" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-32 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <div className="flex items-center gap-3 mb-6">
                <span className="tick-label">A-001 · Job Intelligence</span>
                <span className="h-px flex-1 bg-ink/20 max-w-[180px]" />
                <span className="tick-label">2026</span>
              </div>
              <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] text-ink">
                Find the right jobs.<br />
                <span className="text-brand">Write none of the applications.</span>
              </h1>
              <p className="mt-8 max-w-2xl text-base lg:text-lg text-ink-soft leading-relaxed">
                Opplio is the AI career co-pilot that reads your resume, scores every job against your exact skills, and drafts
                a personalised application for each one. You stay in control — we kill the repetition.
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-3">
                {user ? (
                  <Link to="/dashboard" data-testid="hero-cta-dashboard">
                    <Button size="lg" className="h-12 rounded-xl bg-brand hover:bg-brand-hover text-white uppercase tracking-wider text-xs px-6">
                      Go to Dashboard <ArrowRight size={14} weight="bold" className="ml-2" />
                    </Button>
                  </Link>
                ) : (
                  <>
                    <Link to="/auth?mode=register" data-testid="hero-cta">
                      <Button size="lg" className="h-12 rounded-xl bg-brand hover:bg-brand-hover text-white uppercase tracking-wider text-xs px-6">
                        Start for free <ArrowRight size={14} weight="bold" className="ml-2" />
                      </Button>
                    </Link>
                    <Link to="/auth" data-testid="hero-signin">
                      <Button size="lg" variant="outline" className="h-12 rounded-xl border-slate-200 text-ink hover:bg-ink hover:text-white uppercase tracking-wider text-xs px-6">
                        I already have an account
                      </Button>
                    </Link>
                  </>
                )}
              </div>
              <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-xs text-ink-soft">
                <span className="inline-flex items-center gap-1.5"><CheckCircle size={14} weight="fill" className="text-emerald-600" />Not an auto-apply spammer</span>
                <span className="inline-flex items-center gap-1.5"><CheckCircle size={14} weight="fill" className="text-emerald-600" />Live remote job feed</span>
                <span className="inline-flex items-center gap-1.5"><CheckCircle size={14} weight="fill" className="text-emerald-600" />Edit every draft before sending</span>
              </div>
            </div>

            {/* Match report preview card */}
            <div className="lg:col-span-4">
              <div className="border border-slate-200 bg-white relative">
                <div className="px-4 py-2 border-b border-slate-200 flex items-center justify-between">
                  <span className="tick-label text-ink">Match report</span>
                  <span className="font-mono text-[11px] text-emerald-600 animate-pulse">● LIVE</span>
                </div>
                <div className="p-5">
                  <div className="flex items-start justify-between gap-4 pb-4 border-b border-border">
                    <div>
                      <p className="tick-label">Senior Frontend Engineer</p>
                      <p className="font-display font-bold text-lg mt-1 leading-tight">Linear HQ · Remote (EU)</p>
                    </div>
                    <div className="font-mono font-black text-4xl text-emerald-600 leading-none">92</div>
                  </div>
                  <div className="mt-4 space-y-3 text-sm">
                    <div className="flex items-start gap-2">
                      <span className="w-1 h-5 bg-brand mt-0.5" />
                      <p className="text-ink-soft">5 yrs React + TypeScript matches stack. Shipped design systems at 2 startups.</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="w-1 h-5 bg-brand mt-0.5" />
                      <p className="text-ink-soft">Minor gap: Production Rust exposure. Easy to address.</p>
                    </div>
                  </div>
                  <div className="mt-5 pt-4 border-t border-border">
                    <span className="tick-label">Draft preview</span>
                    <p className="mt-2 text-sm text-ink leading-relaxed italic">
                      "Hi Linear team — your focus on performance and craft is exactly where I operate best…"
                    </p>
                  </div>
                </div>
                <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
                  <span className="tick-label">Ready to apply</span>
                  <ArrowRight size={14} weight="bold" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* LIVE JOBS SECTION */}
      <section className="border-b border-border">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <span className="tick-label text-emerald-600">Live feed</span>
              </div>
              <h2 className="font-display font-black text-3xl lg:text-4xl tracking-tighter">
                Jobs available right now
              </h2>
              <p className="mt-2 text-sm text-ink-soft">Real listings pulled from the live job feed — sign up to get AI-matched to your profile.</p>
            </div>
            <Link to="/auth?mode=register">
              <Button variant="outline" className="rounded-xl border-slate-200 text-ink hover:bg-ink hover:text-white uppercase tracking-wider text-xs px-5 h-10">
                See your matches <ArrowRight size={12} weight="bold" className="ml-2" />
              </Button>
            </Link>
          </div>

          {jobsLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="border border-slate-200/60 bg-white/50 backdrop-blur-sm shadow-sm bg-slate-50/40 h-44 animate-pulse" />
              ))}
            </div>
          ) : liveJobs.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {liveJobs.map((job) => (
                <LiveJobCard key={job.id} job={job} />
              ))}
            </div>
          ) : (
            <div className="border border-slate-200/60 bg-white/50 backdrop-blur-sm shadow-sm p-10 text-center text-ink-soft text-sm">
              Live feed temporarily unavailable — <Link to="/auth?mode=register" className="text-brand underline">sign up</Link> to access full job discovery.
            </div>
          )}

          <p className="mt-6 text-center text-xs text-ink-mute">
            Sign up free to unlock AI match scoring, skill gap analysis and personalised application drafts for every listing.
          </p>
        </div>
      </section>

      {/* STEPS */}
      <section className="border-b border-border">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <span className="tick-label">How it works</span>
              <h2 className="mt-3 font-display font-black text-4xl lg:text-5xl tracking-tighter max-w-3xl">
                Four steps between today's resume<br />and tomorrow's interview.
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-t border-l border-border">
            {steps.map((s) => (
              <div key={s.num} className="border-r border-b border-border p-6 lg:p-8 bg-white">
                <div className="flex items-center justify-between mb-6">
                  <span className="font-mono text-xs text-ink-mute">{s.num}</span>
                  <s.icon size={22} weight="duotone" className="text-brand" />
                </div>
                <h3 className="font-display font-bold text-xl tracking-tight">{s.title}</h3>
                <p className="mt-3 text-sm text-ink-soft leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DIFFERENTIATOR */}
      <section className="border-b border-border bg-slate-50/40">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <span className="tick-label">Principles</span>
            <h2 className="mt-3 font-display font-black text-4xl lg:text-5xl tracking-tighter">
              Not an auto-apply bot. A thinking partner.
            </h2>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-0 border-l border-t border-border">
            {[
              { t: "Signal over volume", b: "We surface fewer, better jobs — ranked against your real experience.", i: Target },
              { t: "Always editable", b: "Every AI draft is a starting point. Rewrite it, shorten it, make it yours.", i: Sparkle },
              { t: "You click Apply", b: "We never submit anything without you. Control stays with the candidate.", i: ShieldCheck },
              { t: "Transparent matching", b: "Each score shows why it matched and which skills are gaps.", i: Compass },
            ].map((x) => (
              <div key={x.t} className="border-r border-b border-border p-6 bg-white">
                <x.i size={20} weight="duotone" className="text-brand mb-3" />
                <h4 className="font-display font-bold text-lg tracking-tight">{x.t}</h4>
                <p className="mt-2 text-sm text-ink-soft leading-relaxed">{x.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="border-b border-border">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28 text-center">
          <span className="tick-label">Start the scan</span>
          <h2 className="mt-4 font-display font-black text-4xl lg:text-6xl tracking-tighter max-w-3xl mx-auto">
            Upload a resume. Get matched. Apply better.
          </h2>
          <p className="mt-6 text-ink-soft max-w-xl mx-auto">
            No credit card. No spam applications. Just smarter job hunting.
          </p>
          <Link to="/auth?mode=register" className="inline-block mt-10" data-testid="final-cta">
            <Button size="lg" className="h-12 rounded-xl bg-brand hover:bg-brand-hover text-white uppercase tracking-wider text-xs px-8">
              Launch Opplio <ArrowRight size={14} weight="bold" className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <footer className="py-10 px-6 lg:px-10 max-w-[1400px] mx-auto text-xs text-ink-mute flex flex-wrap items-center justify-between gap-2">
        <span className="font-mono">OPPLIO · AI · V1.0</span>
        <span>Built with care. Control stays with you.</span>
      </footer>
    </div>
  );
}
